import type { AxiosError } from 'axios';
import type {
  AddressEditErrorCode,
  AppVersionPolicyErrorCode,
  AppVersionPolicyField,
  ChangeRequestErrorCode,
  KycCheckErrorCode,
  OpsUserSummary,
  OwnershipErrorCode,
  PaymentExtendedErrorCode,
  ParticipantKycRole,
  PhoneErrorCode,
  TripStartErrorCode,
  TruckTypeErrorCode,
  TruckTypeField,
} from './types';

const OWNERSHIP_ERROR_CODES: readonly OwnershipErrorCode[] = [
  'OWNERSHIP_CONFLICT',
  'CAP_REACHED',
  'SHIPMENT_CLOSED',
  'ASSIGNEE_UNAVAILABLE',
  'OWNERSHIP_FOLLOWS_ORDER',
];

const PAYMENT_ERROR_CODES: readonly PaymentExtendedErrorCode[] = [
  'EXEMPTION_REASON_REQUIRED',
  'INVALID_EXEMPTION_REASON',
  'EXEMPTION_CONFIRMATION_REQUIRED',
  'EXEMPTION_STATE_REQUIRED',
  'INVALID_EXEMPTION_REFERENCE',
  'EXEMPTION_GST_CONFLICT',
  'EXEMPTION_TREATMENT_REQUIRED',
  'EXEMPTION_STATE_MISMATCH',
  'EXEMPTION_BUYER_DETAILS_REQUIRED',
  'SHIPPER_GSTIN_REQUIRED',
  'SHIPPER_GSTIN_INVALID',
  'GST_OPTION_NOT_ALLOWED',
  'INVOICE_NOT_FOUND',
  'INVOICE_STATE_CHANGED',
  'INVALID_INVOICE_REFERENCES',
  'PAYEE_MISMATCH',
  'PAYEE_NOT_READY',
  'SELF_APPROVAL_NOT_ALLOWED',
  'ESCALATION_REASON_REQUIRED',
  'PAYEE_NOT_SUPPORTED',
  'ADVANCE_NOT_AVAILABLE',
  'CHARGES_PENDING',
  'PAYMENT_OVER_ALLOCATED',
  'CREDIT_EXCEEDED',
  'REBUILD_IN_PROGRESS',
  'CLAIM_STATE_CHANGED',
  'CLAIM_EXCEEDS_OUTSTANDING',
  'PAID_AT_FUTURE',
  'LEGACY_ORDER',
  'FORBIDDEN',
  'MANAGER_ONLY',
  'PROOF_UPLOAD_INVALID',
  'PAYMENT_STATE_CHANGED',
  'IDEMPOTENCY_RETRY',
  'IDEMPOTENCY_CONFLICT',
  'INVOICE_ALREADY_ISSUED',
  'BALANCE_ALREADY_REQUESTED',
  'ORDER_NOT_LOADED',
  'ADVANCE_AFTER_POD',
  'BALANCE_BEFORE_POD',
  'ADVANCE_EXCEEDED',
  'ALLOCATIONS_EXCEED_AMOUNT',
  'ALLOCATION_NOT_FOUND',
  'ALLOCATION_STATE_CHANGED',
  'AMOUNT_NOT_WHOLE_RUPEES',
  'CANNOT_VOID_OUT_ALLOCATION',
  'CHARGE_NOT_FOUND',
  'CHARGE_STATE_CHANGED',
  'CLIENT_REQUEST_ID_REQUIRED',
  'DATE_RANGE_REQUIRED',
  'DESCRIPTION_REQUIRED',
  'DESCRIPTION_TOO_LONG',
  'INVALID_ALLOCATION_AMOUNT',
  'INVALID_ALLOCATION_COUNT',
  'INVALID_ALLOCATION_LINE',
  'INVALID_AMOUNT',
  'INVALID_CHARGE_TYPE',
  'INVALID_DATE_RANGE',
  'INVALID_METRICS_RANGE',
  'INVALID_PAID_AT',
  'INVALID_PAGE',
  'INVALID_PARAMS',
  'INVALID_PAYMENT_STATE',
  'INVALID_PURPOSE',
  'INVALID_REASON',
  'INVALID_REQUEST',
  'METHOD_REQUIRED',
  'NOTE_REQUIRED',
  'NOTE_TOO_LONG',
  'ORDER_CANCELLED',
  'ORDER_NOT_FOUND',
  'ORDER_REQUIRED',
  'PARTNER_NOT_FOUND',
  'PARTNER_REQUIRED',
  'PAYMENT_NOT_FOUND',
  'POD_NOT_VERIFIED',
  'PROOF_NOT_FOUND',
  'PROOF_REQUIRED',
  'REASON_REQUIRED',
  'SHIPPER_MISMATCH',
  'SHIPPER_NOT_FOUND',
  'SHIPPER_REQUIRED',
  'UTR_REQUIRED',
  'SHIPMENT_NOT_FOUND',
  'INVALID_BILL_NUMBER',
  'INVALID_PAYMENT_TERMS',
  'INVALID_GST_PAIR',
  'GST_PAIR_NOT_ALLOWED',
  'AMOUNT_TOO_PRECISE',
  'UNAUTHORIZED',
];

const PHONE_ERROR_CODES: readonly PhoneErrorCode[] = ['PHONE_INVALID_INDIA'];

const ADDRESS_EDIT_ERROR_CODES: readonly AddressEditErrorCode[] = ['ADDRESS_EDIT_NOT_ALLOWED'];

const KYC_CHECK_ERROR_CODES: readonly KycCheckErrorCode[] = ['KYC_CHECK_OVERRIDE_NOT_ALLOWED'];

/** Change-request approve / reject refusals (#281); copy lives in `changeRequestDisplay.ts`. */
export const CHANGE_REQUEST_ERROR_CODES: readonly ChangeRequestErrorCode[] = [
  'CHANGE_REQUEST_NOT_PENDING',
  'CHANGE_REQUEST_NOT_FOUND',
  'CHANGE_REQUEST_REASON_REQUIRED',
];

/** Shipment action refusals (rhino-ops-console#213 Contract); copy lives in `actionFailure.ts`. */
export type ShipmentActionErrorCode =
  | 'SHIPMENT_TRIP_STARTED'
  | 'SHIPMENT_ALREADY_CLOSED'
  | 'SHIPMENT_POD_NOT_VERIFIED'
  | 'SHIPMENT_REASON_REQUIRED'
  | 'SHIPMENT_BIDDING_OPEN'
  | 'SHIPMENT_INVALID_TRANSITION';

export const SHIPMENT_ACTION_ERROR_CODES: readonly ShipmentActionErrorCode[] = [
  'SHIPMENT_TRIP_STARTED',
  'SHIPMENT_ALREADY_CLOSED',
  'SHIPMENT_POD_NOT_VERIFIED',
  'SHIPMENT_REASON_REQUIRED',
  'SHIPMENT_BIDDING_OPEN',
  'SHIPMENT_INVALID_TRANSITION',
];

/** Shipper-final edit refusals (rhino-ops-console#221 Contract); copy lives in `shipperFreight.ts`. */
export type ShipperFreightErrorCode = 'SHIPPER_INVOICE_ISSUED' | 'NOT_AWARDED';

export const SHIPPER_FREIGHT_ERROR_CODES: readonly ShipperFreightErrorCode[] = [
  'SHIPPER_INVOICE_ISSUED',
  'NOT_AWARDED',
];

/** Partner-price refusals (#288, Contract dnm-work#30); copy lives in `features/shipments/partnerOfferPrice.ts`. */
export type PartnerPriceErrorCode = 'NOT_OPEN_FOR_BIDS';

export const PARTNER_PRICE_ERROR_CODES: readonly PartnerPriceErrorCode[] = ['NOT_OPEN_FOR_BIDS'];

/** Partner load alert refusals (#289, Contract dnm-work#41); copy lives in `features/shipments/partnerAlert.ts`. */
export type PartnerAlertErrorCode = 'ALREADY_NOTIFIED' | 'NO_WHATSAPP_NUMBER' | 'SHIPMENT_NOT_POSTED' | 'CLIENT_REQUEST_ID_REUSED';

export const PARTNER_ALERT_ERROR_CODES: readonly PartnerAlertErrorCode[] = [
  'ALREADY_NOTIFIED',
  'NO_WHATSAPP_NUMBER',
  'SHIPMENT_NOT_POSTED',
  'CLIENT_REQUEST_ID_REUSED',
];

/** Record-delay refusals (#294, Contract dnm-work#150); copy lives in `features/shipments/delayReason.ts`. */
export type DelayErrorCode = 'DELAY_REASON_TEXT_REQUIRED' | 'DELAY_TIME_IN_PAST' | 'SHIPMENT_NOT_IN_TRANSIT';

export const DELAY_ERROR_CODES: readonly DelayErrorCode[] = [
  'DELAY_REASON_TEXT_REQUIRED',
  'DELAY_TIME_IN_PAST',
  'SHIPMENT_NOT_IN_TRANSIT',
];

/** Stop refusals (dnm-work#165 C2, rhino-api#378); copy lives in `actionFailure.ts`. */
export type StopErrorCode =
  | 'STOP_ALREADY_OPEN'
  | 'STOP_NOT_ALLOWED'
  | 'STOP_CLOSED'
  | 'STOP_NOT_FOUND'
  | 'VALIDATION_FAILED';

export const STOP_ERROR_CODES: readonly StopErrorCode[] = [
  'STOP_ALREADY_OPEN',
  'STOP_NOT_ALLOWED',
  'STOP_CLOSED',
  'STOP_NOT_FOUND',
  'VALIDATION_FAILED',
];

/** Fields a stop or detention-setting VALIDATION_FAILED names (allow-listed; anything else is dropped). */
export type StopField = 'reason' | 'payer' | 'note' | 'place' | 'expectedResumeAt' | 'value';

const STOP_FIELDS: readonly StopField[] = ['reason', 'payer', 'note', 'place', 'expectedResumeAt', 'value'];

/** Truck-type refusals (#249 Contract sections 3–4): the Contract sentences, kept here so backend text never reaches the UI. */
const TRUCK_TYPE_COPY: Record<TruckTypeErrorCode, string> = {
  TRUCK_TYPE_INVALID: 'Check the entered information and try again.',
  TRUCK_CATEGORY_UNKNOWN: 'Pick a category from the list.',
  TRUCK_TYPE_NOT_FOUND: 'This truck type no longer exists. Reload the list.',
  TRUCK_TYPE_NAME_TAKEN: 'A truck type with this name already exists.',
  TRUCK_TYPE_STALE: 'Someone else changed this truck type. Reload and try again.',
  TRUCK_TYPE_CONFLICT: 'Pick a truck type or type your own, not both.',
  TRUCK_TYPE_OTHER_INVALID: 'Describe the truck in 2 to 40 letters, for example 14 ft canter.',
  TRUCK_TYPE_UNKNOWN: 'This truck type is not available. Pick another one or choose Other.',
};

const TRUCK_TYPE_ERROR_CODES = Object.keys(TRUCK_TYPE_COPY) as TruckTypeErrorCode[];

/** TRUCK_TYPE_INVALID names the field; the sentence for that field. */
export const TRUCK_TYPE_FIELD_COPY: Record<TruckTypeField, string> = {
  categoryId: 'Pick a category from the list.',
  name: 'Enter a truck name of 2 to 60 letters.',
  capacityTons: 'Keep capacity to 30 letters or fewer.',
  lengthFt: 'Keep length to 30 letters or fewer.',
  idealFor: 'Keep "Ideal for" to 200 letters or fewer.',
  sortOrder: 'Enter a whole number for the order.',
};

const TRUCK_TYPE_FIELDS = Object.keys(TRUCK_TYPE_FIELD_COPY) as TruckTypeField[];

function truckTypeRefusal(error: AxiosError): { message: string; detail: RequestDetail } | null {
  const body = error.response?.data;
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  const code = TRUCK_TYPE_ERROR_CODES.find((c) => c === b.code);
  if (!code) return null;
  const field = code === 'TRUCK_TYPE_INVALID' ? TRUCK_TYPE_FIELDS.find((f) => f === b.field) : undefined;
  return {
    message: field ? TRUCK_TYPE_FIELD_COPY[field] : TRUCK_TYPE_COPY[code],
    detail: field ? { code, field } : { code },
  };
}

/** 400 APP_VERSION_POLICY_INVALID names the field (dnm-work#170 C4); the sentence for that field. */
export const APP_VERSION_POLICY_FIELD_COPY: Record<AppVersionPolicyField, string> = {
  minSupportedVersion: 'Enter a version like 1.4.0 or 1.4.0+57, no higher than the latest version.',
  latestVersion: 'Enter a version like 1.4.0 or 1.4.0+57.',
  storeUrl: 'Enter a link that starts with https:// (up to 500 characters).',
  'releaseNotes.en': 'Keep the note to 140 characters.',
  'releaseNotes.hi': 'Keep the note to 140 characters.',
};

const APP_VERSION_POLICY_FIELDS = Object.keys(APP_VERSION_POLICY_FIELD_COPY) as AppVersionPolicyField[];

/** App version policy refusals (#338, C4) and the manager-only 403 (rhino-api R44); never the API's own text. */
function appVersionPolicyRefusal(error: AxiosError): { message: string; detail: RequestDetail } | null {
  const body = error.response?.data;
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (error.response?.status === 403 && b.code === 'MANAGER_ONLY') {
    return { message: MANAGER_ONLY_MESSAGE, detail: { code: 'MANAGER_ONLY' } };
  }
  if (b.code !== 'APP_VERSION_POLICY_INVALID') return null;
  const field = APP_VERSION_POLICY_FIELDS.find((f) => f === b.field);
  return {
    message: field ? APP_VERSION_POLICY_FIELD_COPY[field] : 'Check the entered information and try again.',
    detail: field ? { code: 'APP_VERSION_POLICY_INVALID', field } : { code: 'APP_VERSION_POLICY_INVALID' },
  };
}

const PARTICIPANT_ROLES: readonly ParticipantKycRole[] = ['SHIPPER', 'DRIVER', 'PARTNER'];

type RequestErrorCode =
  | AddressEditErrorCode
  | AppVersionPolicyErrorCode
  | ChangeRequestErrorCode
  | KycCheckErrorCode
  | DelayErrorCode
  | OwnershipErrorCode
  | PartnerAlertErrorCode
  | PartnerPriceErrorCode
  | PaymentExtendedErrorCode
  | PhoneErrorCode
  | ShipmentActionErrorCode
  | ShipperFreightErrorCode
  | StopErrorCode
  | TripStartErrorCode
  | TruckTypeErrorCode;

interface RequestDetail {
  code?: RequestErrorCode;
  owner?: OpsUserSummary | null;
  shipmentId?: number;
  role?: ParticipantKycRole;
  userId?: number | null;
  field?: TruckTypeField | StopField | AppVersionPolicyField;
}

export class DnmRequestFailure extends Error {
  readonly response?: { status: number };
  /** Allow-listed machine-readable error code from the API body (never free text). */
  readonly code?: RequestErrorCode;
  readonly shipmentId?: number;
  /** Current owner, sent with OWNERSHIP_CONFLICT. */
  readonly owner?: OpsUserSummary | null;
  /** Trip participant refused by KYC_NOT_VERIFIED; `userId` is null when nobody holds the role. */
  readonly role?: ParticipantKycRole;
  readonly userId?: number | null;
  /** Form field named by TRUCK_TYPE_INVALID, a stop VALIDATION_FAILED or APP_VERSION_POLICY_INVALID (allow-listed). */
  readonly field?: TruckTypeField | StopField | AppVersionPolicyField;
  constructor(message: string, status?: number, detail: RequestDetail = {}) {
    super(message);
    this.name = 'DnmRequestFailure';
    if (status != null) this.response = { status };
    if (detail.code) this.code = detail.code;
    if (detail.shipmentId != null) this.shipmentId = detail.shipmentId;
    if (detail.owner !== undefined) this.owner = detail.owner;
    if (detail.role) this.role = detail.role;
    if (detail.userId !== undefined) this.userId = detail.userId;
    if (detail.field) this.field = detail.field;
  }
}

function pickSummary(value: unknown): OpsUserSummary | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== 'number') return null;
  const str = (x: unknown) => (typeof x === 'string' ? x : null);
  return {
    id: v.id,
    firstName: str(v.firstName),
    lastName: str(v.lastName),
    email: str(v.email) ?? '',
    role: v.role === 'OPS_MANAGER' ? 'OPS_MANAGER' : 'OPS_EXECUTIVE',
    status: v.status === 'AWAY' || v.status === 'INACTIVE' ? v.status : 'ACTIVE',
  };
}

function ownershipDetail(error: AxiosError): {
  code?: OwnershipErrorCode;
  owner?: OpsUserSummary | null;
} {
  const body = error.response?.data;
  if (!body || typeof body !== 'object') return {};
  const raw = (body as Record<string, unknown>).code;
  const code = OWNERSHIP_ERROR_CODES.find((c) => c === raw);
  if (!code) return {};
  if (code === 'OWNERSHIP_CONFLICT') {
    return { code, owner: pickSummary((body as Record<string, unknown>).owner) };
  }
  return { code };
}

const KYC_REFUSAL_COPY: Record<ParticipantKycRole, string> = {
  SHIPPER:
    "The shipper's KYC is not verified, so this bid can't be accepted. Verify the shipper's KYC, then try again.",
  PARTNER:
    "The partner's KYC is not verified, so this can't go ahead. Verify the partner's KYC, then try again.",
  DRIVER:
    "The driver's KYC, truck RC or truck photo is not verified, so the trip can't start. Verify them, then try again.",
};

/** Console copy for a KYC_NOT_VERIFIED refusal, by the role rhino-api names (R18 gates, rhino-ops-console#228). */
export function kycRefusalCopy(role: ParticipantKycRole): string {
  return KYC_REFUSAL_COPY[role];
}

/**
 * KYC_NOT_VERIFIED is the one 400 whose `error` the API authors as user-facing copy
 * (rhino-ops-console#210 Contract; no phone numbers). A known role with a person gets the
 * console's role copy; an unknown role, or an empty slot (`userId` null: nobody to verify),
 * keeps the API's own text. Every other body stays hidden.
 */
function tripStartRefusal(error: AxiosError): { message: string; detail: RequestDetail } | null {
  if (error.response?.status !== 400) return null;
  const body = error.response.data;
  if (!body || typeof body !== 'object') return null;
  const b = body as Record<string, unknown>;
  if (b.code !== 'KYC_NOT_VERIFIED' || typeof b.error !== 'string' || !b.error.trim()) return null;
  const role = PARTICIPANT_ROLES.find((r) => r === b.role);
  const userId = typeof b.userId === 'number' && Number.isSafeInteger(b.userId) ? b.userId : null;
  const message = role && userId != null ? kycRefusalCopy(role) : b.error;
  return { message, detail: { code: 'KYC_NOT_VERIFIED', role, userId } };
}

function requestDetail(error: AxiosError): RequestDetail {
  const ownership = ownershipDetail(error);
  if (ownership.code) return ownership;
  const body = error.response?.data;
  if (!body || typeof body !== 'object') return {};
  const raw = (body as Record<string, unknown>).code;
  const code =
    PAYMENT_ERROR_CODES.find((code) => code === raw) ??
    PHONE_ERROR_CODES.find((code) => code === raw) ??
    ADDRESS_EDIT_ERROR_CODES.find((code) => code === raw) ??
    KYC_CHECK_ERROR_CODES.find((code) => code === raw) ??
    CHANGE_REQUEST_ERROR_CODES.find((code) => code === raw) ??
    SHIPMENT_ACTION_ERROR_CODES.find((code) => code === raw) ??
    SHIPPER_FREIGHT_ERROR_CODES.find((code) => code === raw) ??
    PARTNER_PRICE_ERROR_CODES.find((code) => code === raw) ??
    DELAY_ERROR_CODES.find((code) => code === raw) ??
    PARTNER_ALERT_ERROR_CODES.find((code) => code === raw) ??
    STOP_ERROR_CODES.find((code) => code === raw);
  const rawField = (body as Record<string, unknown>).field;
  const field = code === 'VALIDATION_FAILED' ? STOP_FIELDS.find((f) => f === rawField) : undefined;
  const diagnostic = (body as Record<string, unknown>).error;
  const match =
    ['ADVANCE_EXCEEDED', 'ADVANCE_AFTER_POD'].includes(code ?? '') && typeof diagnostic === 'string'
      ? diagnostic.match(/(?:order\s+#?|on order\s+)(\d+)/i)
      : null;
  const shipmentId = match ? Number(match[1]) : undefined;
  return {
    code,
    ...(field ? { field } : {}),
    ...(shipmentId != null && Number.isSafeInteger(shipmentId) ? { shipmentId } : {}),
  };
}

/** True when the API rejected a new primary phone under the India rule. */
export function isPhoneInvalidIndia(error: unknown): boolean {
  return error instanceof DnmRequestFailure && error.code === 'PHONE_INVALID_INDIA';
}

/** User-facing copy is chosen locally; backend diagnostics stay out of the UI. */
/** The 403 copy, also shown by screens that know up front the person has no access (#300 Profit). */
/** True for a request the API refused with 403: retrying cannot help, so screens show no "Try again". */
export function isForbidden(error: unknown): boolean {
  return error instanceof DnmRequestFailure && error.response?.status === 403;
}

export const NO_ACCESS_MESSAGE = 'You don’t have access to this. Contact DNM Support if you need help.';
/**
 * 403 MANAGER_ONLY (dnm-work#161 Contract C2): an ops executive tried a manager-only write.
 * The same words the API sends, chosen locally because this file never renders server text.
 */
export const MANAGER_ONLY_MESSAGE = 'Only an ops manager can do this. Ask your ops manager.';

export function safeRequestFailure(error: AxiosError): DnmRequestFailure {
  const status = error.response?.status;
  const method = error.config?.method?.toUpperCase();
  const mutation = method != null && !['GET', 'HEAD', 'OPTIONS'].includes(method);
  if (!error.response && mutation) {
    return new DnmRequestFailure(
      'We couldn’t confirm whether this action completed. Check its status before trying again.',
    );
  }
  const refusal = tripStartRefusal(error);
  if (refusal) return new DnmRequestFailure(refusal.message, status, refusal.detail);
  const truckRefusal = truckTypeRefusal(error);
  if (truckRefusal) return new DnmRequestFailure(truckRefusal.message, status, truckRefusal.detail);
  const policyRefusal = appVersionPolicyRefusal(error);
  if (policyRefusal) return new DnmRequestFailure(policyRefusal.message, status, policyRefusal.detail);
  const message =
    status === 401
      ? 'Your session has expired. Sign in again.'
      : status === 403
        ? NO_ACCESS_MESSAGE
        : status === 404
          ? 'This item is no longer available.'
          : status === 409
            ? 'This information changed. Refresh and check its current status.'
            : status === 400 || status === 422
              ? 'Check the entered information and try again.'
              : status === 429
                ? 'Too many attempts. Wait a moment and try again.'
                : status && status >= 500
                  ? 'DNM is having trouble right now. Check the current status before trying again.'
                  : 'We couldn’t connect to DNM. Check your connection and try again.';
  return new DnmRequestFailure(message, status, requestDetail(error));
}
